import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Tabs,
  Tab,
  Chip,
  Paper,
  Divider,
  Alert,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ContentCopy,
  Add,
  Schedule,
  Warning,
  Delete,
  Edit,
  CalendarMonth,
} from '@mui/icons-material';
import { useInput, useTranslate } from 'react-admin';

interface DayHours {
  open: string;
  close: string;
}

interface SpecialDate {
  date: string;
  name: string;
  hours: {
    open?: string;
    close?: string;
    closed?: boolean;
  };
}

interface OperatingHours {
  monday?: DayHours;
  tuesday?: DayHours;
  wednesday?: DayHours;
  thursday?: DayHours;
  friday?: DayHours;
  saturday?: DayHours;
  sunday?: DayHours;
  publicHolidays?: {
    closed?: boolean;
    open?: string;
    close?: string;
  };
  specialDates?: SpecialDate[];
}

type DayName = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

const DAYS: DayName[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Day labels will be translated dynamically in the component

// Templates will be created dynamically with translations inside the component

export const OperatingHoursField: React.FC = () => {
  const translate = useTranslate();
  const input = useInput({ source: 'operatingHours' });
  const [activeTab, setActiveTab] = useState(0);
  const [showSpecialDates, setShowSpecialDates] = useState(false);
  const [selectedDays, setSelectedDays] = useState<DayName[]>([]);
  const [specialDateDialog, setSpecialDateDialog] = useState(false);
  const [editingSpecialDate, setEditingSpecialDate] = useState<SpecialDate | null>(null);
  const [newSpecialDate, setNewSpecialDate] = useState<SpecialDate>({
    date: '',
    name: '',
    hours: { open: '09:00', close: '17:00' }
  });

  // Initialize with empty hours if not set
  const operatingHours: OperatingHours = input.field.value || {};
  const specialDates = operatingHours.specialDates || [];

  // Helper function to get translated day label
  const getDayLabel = (day: DayName) => {
    return translate(`resources.sites.operatingHours.days.${day}`);
  };

  // Helper function to get translated day abbreviation
  const getDayAbbr = (day: DayName) => {
    const abbr = day.slice(0, 3) as 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
    return translate(`resources.sites.operatingHours.days.${abbr}`);
  };

  // Preset templates with translations
  const PRESET_TEMPLATES = [
    {
      name: translate('resources.sites.operatingHours.quickTemplates.247'),
      hours: { open: '00:00', close: '23:59' },
      days: DAYS,
    },
    {
      name: translate('resources.sites.operatingHours.quickTemplates.businessHours'),
      hours: { open: '09:00', close: '17:00' },
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as DayName[],
    },
    {
      name: translate('resources.sites.operatingHours.quickTemplates.extendedHours'),
      hours: { open: '06:00', close: '00:00' },
      days: DAYS,
    },
  ];

  const handleTimeChange = (day: DayName, field: 'open' | 'close', value: string) => {
    const updated = {
      ...operatingHours,
      [day]: {
        ...operatingHours[day],
        [field]: value,
      },
    };
    input.field.onChange(updated);
  };

  const handleDayToggle = (day: DayName) => {
    if (operatingHours[day]) {
      // Remove the day (mark as closed)
      const updated = { ...operatingHours };
      delete updated[day];
      input.field.onChange(updated);
    } else {
      // Add the day with default hours
      const updated = {
        ...operatingHours,
        [day]: { open: '09:00', close: '17:00' },
      };
      input.field.onChange(updated);
    }
  };

  const applyTemplate = (template: typeof PRESET_TEMPLATES[0]) => {
    const updated = { ...operatingHours };
    template.days.forEach(day => {
      updated[day] = { ...template.hours };
    });
    input.field.onChange(updated);
  };

  const copyHours = (fromDay: DayName, toDays: DayName[]) => {
    if (!operatingHours[fromDay]) return;
    
    const updated = { ...operatingHours };
    toDays.forEach(day => {
      updated[day] = { ...operatingHours[fromDay]! };
    });
    input.field.onChange(updated);
  };

  const handleAddSpecialDate = () => {
    if (!newSpecialDate.date || !newSpecialDate.name) return;
    
    const updated = {
      ...operatingHours,
      specialDates: [
        ...(operatingHours.specialDates || []),
        newSpecialDate
      ]
    };
    input.field.onChange(updated);
    setNewSpecialDate({
      date: '',
      name: '',
      hours: { open: '09:00', close: '17:00' }
    });
    setSpecialDateDialog(false);
  };

  const handleUpdateSpecialDate = () => {
    if (!editingSpecialDate || !newSpecialDate.date || !newSpecialDate.name) return;
    
    const updated = {
      ...operatingHours,
      specialDates: (operatingHours.specialDates || []).map(sd =>
        sd.date === editingSpecialDate.date ? newSpecialDate : sd
      )
    };
    input.field.onChange(updated);
    setEditingSpecialDate(null);
    setNewSpecialDate({
      date: '',
      name: '',
      hours: { open: '09:00', close: '17:00' }
    });
    setSpecialDateDialog(false);
  };

  const handleDeleteSpecialDate = (date: string) => {
    const updated = {
      ...operatingHours,
      specialDates: (operatingHours.specialDates || []).filter(sd => sd.date !== date)
    };
    input.field.onChange(updated);
  };

  const handleEditSpecialDate = (specialDate: SpecialDate) => {
    setEditingSpecialDate(specialDate);
    setNewSpecialDate(specialDate);
    setSpecialDateDialog(true);
  };


  const is24Hours = (hours: DayHours) => {
    return hours.open === '00:00' && (hours.close === '23:59' || hours.close === '24:00' || hours.close === '00:00');
  };

  const formatTimeDisplay = (time: string) => {
    if (time === '24:00' || time === '23:59') return translate('resources.sites.operatingHours.preview.nextDay');
    const [hour, minute] = time.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  const getHoursDisplay = (day: DayName) => {
    const hours = operatingHours[day];
    if (!hours) return translate('resources.sites.operatingHours.status.closed');
    if (is24Hours(hours)) return translate('resources.sites.operatingHours.status.247');
    return `${formatTimeDisplay(hours.open)} - ${formatTimeDisplay(hours.close)}`;
  };

  const getStatusColor = (day: DayName) => {
    const hours = operatingHours[day];
    if (!hours) return 'error';
    if (is24Hours(hours)) return 'success';
    return 'primary';
  };

  // Group consecutive days with same hours for preview
  const getGroupedHours = () => {
    const groups: Array<{ days: DayName[], hours: DayHours | null }> = [];

    DAYS.forEach(day => {
      const hours = operatingHours[day];
      const lastGroup = groups[groups.length - 1];

      if (lastGroup && JSON.stringify(lastGroup.hours) === JSON.stringify(hours)) {
        lastGroup.days.push(day);
      } else {
        groups.push({ days: [day], hours: hours || null });
      }
    });

    return groups;
  };

  // Helper to format grouped days text for preview
  const formatGroupedDaysText = (days: DayName[]) => {
    if (days.length === 7) {
      return translate('resources.sites.operatingHours.preview.everyDay');
    } else if (days.length === 5 && days[0] === 'monday' && days[4] === 'friday') {
      return translate('resources.sites.operatingHours.preview.monFri');
    } else if (days.length === 2 && days[0] === 'saturday' && days[1] === 'sunday') {
      return translate('resources.sites.operatingHours.preview.satSun');
    } else {
      return days.map(d => getDayAbbr(d)).join(', ');
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
        <Schedule />
        {translate('resources.sites.fields.operatingHours')}
      </Typography>

        {/* Quick Templates */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {translate('resources.sites.operatingHours.quickTemplates.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {PRESET_TEMPLATES.map(template => (
              <Button
                key={template.name}
                variant="outlined"
                size="small"
                onClick={() => applyTemplate(template)}
              >
                {template.name}
              </Button>
            ))}
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={() => input.field.onChange({})}
            >
              {translate('resources.sites.operatingHours.quickTemplates.clearAll')}
            </Button>
          </Box>
        </Box>

        {/* View Tabs */}
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab label={translate('resources.sites.operatingHours.tabs.individualDays')} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: { xs: 'auto', sm: 90 }, px: { xs: 1, sm: 2 } }} />
          <Tab label={translate('resources.sites.operatingHours.tabs.quickEdit')} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: { xs: 'auto', sm: 90 }, px: { xs: 1, sm: 2 } }} />
          <Tab label={translate('resources.sites.operatingHours.tabs.preview')} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: { xs: 'auto', sm: 90 }, px: { xs: 1, sm: 2 } }} />
        </Tabs>

        {/* Individual Days View */}
        {activeTab === 0 && (
          <Box>
            {DAYS.map(day => {
              const hours = operatingHours[day];
              const isOpen = !!hours;
              
              return (
                <Paper key={day} sx={{ p: { xs: 1.5, sm: 2 }, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isOpen}
                          onChange={() => handleDayToggle(day)}
                          size="small"
                        />
                      }
                      label={getDayLabel(day)}
                      sx={{ minWidth: { xs: 'auto', sm: 120 }, mr: 0 }}
                    />
                    {!isOpen && (
                      <Chip label={translate('resources.sites.operatingHours.status.closed')} color="error" size="small" />
                    )}
                  </Box>

                  {isOpen && hours && (
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, gap: { xs: 1, sm: 2 } }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          label={translate('resources.sites.operatingHours.timeFields.open')}
                          type="time"
                          value={hours.open}
                          onChange={(e) => handleTimeChange(day, 'open', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label={translate('resources.sites.operatingHours.timeFields.close')}
                          type="time"
                          value={hours.close === '24:00' ? '23:59' : hours.close}
                          onChange={(e) => handleTimeChange(day, 'close', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'space-between', sm: 'flex-start' }, gap: { xs: 0, sm: 1 } }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={is24Hours(hours)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  handleTimeChange(day, 'open', '00:00');
                                  handleTimeChange(day, 'close', '23:59');
                                } else {
                                  handleTimeChange(day, 'open', '09:00');
                                  handleTimeChange(day, 'close', '17:00');
                                }
                              }}
                              size="small"
                            />
                          }
                          label={<Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{translate('resources.sites.operatingHours.status.247')}</Typography>}
                          sx={{ mr: 0 }}
                        />
                        <Button
                          size="small"
                          startIcon={<ContentCopy />}
                          onClick={() => {
                            const otherDays = DAYS.filter(d => d !== day);
                            copyHours(day, otherDays);
                          }}
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' }, whiteSpace: 'nowrap' }}
                        >
                          {translate('resources.sites.operatingHours.actions.copyToAll')}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Box>
        )}

        {/* Quick Edit View */}
        {activeTab === 1 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              {translate('resources.sites.operatingHours.alerts.selectMultipleDays')}
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {translate('resources.sites.operatingHours.actions.selectDays')}
              </Typography>
              <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
                {DAYS.map(day => (
                  <Chip
                    key={day}
                    label={getDayLabel(day)}
                    onClick={() => {
                      if (selectedDays.includes(day)) {
                        setSelectedDays(selectedDays.filter(d => d !== day));
                      } else {
                        setSelectedDays([...selectedDays, day]);
                      }
                    }}
                    color={selectedDays.includes(day) ? 'primary' : 'default'}
                    variant={selectedDays.includes(day) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Box>

            {selectedDays.length > 0 && (
              <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  {translate('resources.sites.operatingHours.actions.setHours')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                    <TextField
                      label={translate('resources.sites.operatingHours.timeFields.open')}
                      type="time"
                      defaultValue="09:00"
                      InputLabelProps={{ shrink: true }}
                      id="quick-open"
                      fullWidth
                    />
                    <TextField
                      label={translate('resources.sites.operatingHours.timeFields.close')}
                      type="time"
                      defaultValue="17:00"
                      InputLabelProps={{ shrink: true }}
                      id="quick-close"
                      fullWidth
                    />
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => {
                      const openInput = document.getElementById('quick-open') as HTMLInputElement;
                      const closeInput = document.getElementById('quick-close') as HTMLInputElement;

                      const updated = { ...operatingHours };
                      selectedDays.forEach(day => {
                        updated[day] = {
                          open: openInput.value || '09:00',
                          close: closeInput.value || '17:00',
                        };
                      });
                      input.field.onChange(updated);
                      setSelectedDays([]);
                    }}
                  >
                    {translate('resources.sites.operatingHours.actions.apply')}
                  </Button>
                </Box>
              </Paper>
            )}
          </Box>
        )}

        {/* Preview View */}
        {activeTab === 2 && (
          <Box>
            <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {translate('resources.sites.operatingHours.preview.hoursSummary')}
              </Typography>
              {getGroupedHours().map((group, index) => {
                const daysText = formatGroupedDaysText(group.days);

                const hoursText = !group.hours
                  ? translate('resources.sites.operatingHours.status.closed')
                  : is24Hours(group.hours)
                  ? translate('resources.sites.operatingHours.status.247')
                  : `${formatTimeDisplay(group.hours.open)} - ${formatTimeDisplay(group.hours.close)}`;

                return (
                  <Box key={index} sx={{ mb: 1 }}>
                    <Typography variant="body1">
                      <strong>{daysText}:</strong> {hoursText}
                    </Typography>
                  </Box>
                );
              })}

              {operatingHours.publicHolidays && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body1">
                    <strong>{translate('resources.sites.operatingHours.publicHolidays.title')}:</strong> {
                      operatingHours.publicHolidays.closed
                        ? translate('resources.sites.operatingHours.status.closed')
                        : `${formatTimeDisplay(operatingHours.publicHolidays.open!)} - ${formatTimeDisplay(operatingHours.publicHolidays.close!)}`
                    }
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {translate('resources.sites.operatingHours.preview.displayPreview')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getGroupedHours()
                  .map(group => {
                    const daysText = formatGroupedDaysText(group.days);

                    const hoursText = !group.hours
                      ? translate('resources.sites.operatingHours.status.closed')
                      : is24Hours(group.hours)
                      ? translate('resources.sites.operatingHours.status.247')
                      : `${formatTimeDisplay(group.hours.open)}-${formatTimeDisplay(group.hours.close)}`;

                    return `${daysText}: ${hoursText}`;
                  })
                  .join(' • ')
                }
              </Typography>

              {operatingHours.publicHolidays && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {translate('resources.sites.operatingHours.publicHolidays.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {operatingHours.publicHolidays.closed
                      ? translate('resources.sites.operatingHours.status.closed')
                      : `${formatTimeDisplay(operatingHours.publicHolidays.open!)} - ${formatTimeDisplay(operatingHours.publicHolidays.close!)}`
                    }
                  </Typography>
                </>
              )}

              {specialDates.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {translate('resources.sites.operatingHours.specialDates.title')}
                  </Typography>
                  {specialDates.map(sd => (
                    <Typography key={sd.date} variant="body2" color="text.secondary">
                      {sd.date} - {sd.name}: {sd.hours.closed ? translate('resources.sites.operatingHours.status.closed') : `${formatTimeDisplay(sd.hours.open!)} - ${formatTimeDisplay(sd.hours.close!)}`}
                    </Typography>
                  ))}
                </>
              )}
            </Paper>

            {/* Status Summary */}
            <Box sx={{ mt: 2, display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
              {DAYS.map(day => (
                <Chip
                  key={day}
                  label={getDayAbbr(day)}
                  size="small"
                  color={getStatusColor(day) as any}
                  variant={operatingHours[day] ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Public Holidays Section */}
        <Divider sx={{ my: 3 }} />
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            {translate('resources.sites.operatingHours.publicHolidays.title')}
          </Typography>
          <Paper sx={{ p: { xs: 1.5, sm: 2 }, backgroundColor: '#f5f5f5' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={operatingHours.publicHolidays !== undefined}
                  onChange={(e) => {
                    const updated = { ...operatingHours };
                    if (e.target.checked) {
                      updated.publicHolidays = { open: '09:00', close: '17:00' };
                    } else {
                      delete updated.publicHolidays;
                    }
                    input.field.onChange(updated);
                  }}
                />
              }
              label={translate('resources.sites.operatingHours.publicHolidays.setDifferentHours')}
            />

            {operatingHours.publicHolidays && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' } }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={operatingHours.publicHolidays.closed || false}
                      onChange={(e) => {
                        const updated = { ...operatingHours };
                        if (e.target.checked) {
                          updated.publicHolidays = { closed: true };
                        } else {
                          updated.publicHolidays = { open: '09:00', close: '17:00' };
                        }
                        input.field.onChange(updated);
                      }}
                    />
                  }
                  label={translate('resources.sites.operatingHours.publicHolidays.closedOnPublicHolidays')}
                />

                {!operatingHours.publicHolidays.closed && (
                  <Box sx={{ display: 'flex', gap: 1, flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                    <TextField
                      label={translate('resources.sites.operatingHours.timeFields.open')}
                      type="time"
                      value={operatingHours.publicHolidays.open || '09:00'}
                      onChange={(e) => {
                        const updated = { ...operatingHours };
                        updated.publicHolidays = {
                          ...operatingHours.publicHolidays,
                          open: e.target.value
                        };
                        input.field.onChange(updated);
                      }}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label={translate('resources.sites.operatingHours.timeFields.close')}
                      type="time"
                      value={(operatingHours.publicHolidays.close === '24:00' ? '23:59' : operatingHours.publicHolidays.close) || '17:00'}
                      onChange={(e) => {
                        const updated = { ...operatingHours };
                        updated.publicHolidays = {
                          ...operatingHours.publicHolidays,
                          close: e.target.value
                        };
                        input.field.onChange(updated);
                      }}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      fullWidth
                    />
                  </Box>
                )}
              </Box>
            )}

            {operatingHours.publicHolidays && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {operatingHours.publicHolidays.closed
                  ? translate('resources.sites.operatingHours.publicHolidays.siteWillBeClosed')
                  : translate('resources.sites.operatingHours.publicHolidays.publicHolidayHours', {
                      hours: `${formatTimeDisplay(operatingHours.publicHolidays.open!)} - ${formatTimeDisplay(operatingHours.publicHolidays.close!)}`
                    })
                }
              </Alert>
            )}
          </Paper>
        </Box>

        {/* Special Dates Section */}
        <Box sx={{ mt: 3 }}>
          <Button
            size="small"
            onClick={() => setShowSpecialDates(!showSpecialDates)}
            startIcon={<CalendarMonth />}
          >
            {showSpecialDates
              ? translate('resources.sites.operatingHours.specialDates.hide')
              : translate('resources.sites.operatingHours.specialDates.show')
            } {translate('resources.sites.operatingHours.specialDates.showSpecialDates')}
          </Button>

          <Collapse in={showSpecialDates}>
            <Paper sx={{ mt: 2, p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle2">
                  {translate('resources.sites.operatingHours.specialDates.subtitle')}
                </Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => {
                    setEditingSpecialDate(null);
                    setNewSpecialDate({
                      date: '',
                      name: '',
                      hours: { open: '09:00', close: '17:00' }
                    });
                    setSpecialDateDialog(true);
                  }}
                >
                  {translate('resources.sites.operatingHours.specialDates.addSpecialDate')}
                </Button>
              </Box>

              {specialDates.length === 0 ? (
                <Alert severity="info">
                  {translate('resources.sites.operatingHours.specialDates.noSpecialDates')}
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {specialDates.map((sd) => (
                    <Paper key={sd.date} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="600">
                            {sd.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {sd.date}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {sd.hours.closed ? (
                              <Chip label={translate('resources.sites.operatingHours.status.closed')} color="error" size="small" />
                            ) : (
                              `${formatTimeDisplay(sd.hours.open!)} - ${formatTimeDisplay(sd.hours.close!)}`
                            )}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditSpecialDate(sd)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteSpecialDate(sd.date)}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </Paper>
          </Collapse>
        </Box>

        {/* Special Date Dialog */}
        <Dialog open={specialDateDialog} onClose={() => setSpecialDateDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingSpecialDate
              ? translate('resources.sites.operatingHours.specialDates.editSpecialDate')
              : translate('resources.sites.operatingHours.specialDates.addSpecialDate')
            }
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label={translate('resources.sites.operatingHours.specialDates.dateLabel')}
                type="date"
                value={newSpecialDate.date}
                onChange={(e) => setNewSpecialDate({ ...newSpecialDate, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label={translate('resources.sites.operatingHours.specialDates.nameLabel')}
                value={newSpecialDate.name}
                onChange={(e) => setNewSpecialDate({ ...newSpecialDate, name: e.target.value })}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={newSpecialDate.hours.closed || false}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewSpecialDate({
                          ...newSpecialDate,
                          hours: { closed: true }
                        });
                      } else {
                        setNewSpecialDate({
                          ...newSpecialDate,
                          hours: { open: '09:00', close: '17:00' }
                        });
                      }
                    }}
                  />
                }
                label={translate('resources.sites.operatingHours.specialDates.closedOnThisDate')}
              />
              {!newSpecialDate.hours.closed && (
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField
                    label={translate('resources.sites.operatingHours.timeFields.open')}
                    type="time"
                    value={newSpecialDate.hours.open || '09:00'}
                    onChange={(e) => setNewSpecialDate({
                      ...newSpecialDate,
                      hours: { ...newSpecialDate.hours, open: e.target.value }
                    })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label={translate('resources.sites.operatingHours.timeFields.close')}
                    type="time"
                    value={(newSpecialDate.hours.close === '24:00' ? '23:59' : newSpecialDate.hours.close) || '17:00'}
                    onChange={(e) => setNewSpecialDate({
                      ...newSpecialDate,
                      hours: { ...newSpecialDate.hours, close: e.target.value }
                    })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSpecialDateDialog(false)}>
              {translate('resources.sites.operatingHours.specialDates.cancel')}
            </Button>
            <Button
              onClick={editingSpecialDate ? handleUpdateSpecialDate : handleAddSpecialDate}
              variant="contained"
              disabled={!newSpecialDate.date || !newSpecialDate.name}
            >
              {editingSpecialDate
                ? translate('resources.sites.operatingHours.specialDates.update')
                : translate('resources.sites.operatingHours.specialDates.add')
              }
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
};